<template>
  <section>
    <v-data-table
      :headers="headers"
      :items="items"
      fixed-header
      :search="search"
      :custom-filter="customFilter"
      :loading="loading"
      :no-data-text="$t('employees.noEmployeesAdded')"
      item-key="id"
      :items-per-page="$vuetify.breakpoint.lgAndDown ? 5 : 10"
      class="table-striped"
      :footer-props="{
        'items-per-page-text': `${$t('employees.footer.itemsPerPage')}`,
        'items-per-page-all-text': `${$t('employees.footer.all')}`,
      }"
    >
      <template v-if="showSelect" v-slot:top>
        <v-row justify="end" class="ma-3">
          <v-btn
            v-if="items.some((i) => i.isSelected)"
            @click="deselectAll"
            color="primary"
            class="mr-3"
            >{{ $t("extTrainings.employees.selectNone") }}</v-btn
          >
          <v-btn v-else @click="selectAll" color="primary" class="mr-3">{{
            $t("extTrainings.employees.selectAll")
          }}</v-btn>
          <v-text-field
            class="flex-grow-1"
            v-model="search"
            prepend-inner-icon="search"
            :label="$t('search.title')"
            single-line
            :background-color="
              $vuetify.theme.themes.light.primary | hexToRgb(0.2)
            "
            solo
            dense
            flat
            hide-details
            clearable
          />
          <v-autocomplete
            class="flex-grow-1 ml-3 ext-training-ac"
            v-model="filters"
            :placeholder="`${$t('extTrainings.filterTags')}`"
            :items="tags"
            :background-color="
              $vuetify.theme.themes.light.primary | hexToRgb(0.2)
            "
            required
            flat
            chips
            deletable-chips
            solo
            dense
            hide-details
            :item-text="getDetailedTagText"
            multiple
            return-object
          >
            <template v-slot:item="{ item, attrs }">
              <v-list-item-action>
                <v-checkbox :input-value="attrs.inputValue"></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{
                  getDetailedTagText(item)
                }}</v-list-item-title>
                <v-list-item-subtitle v-if="item.explanation">
                  <span>{{ item.explanation }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-row>
        <div v-if="showEmails" class="py-3 px-4">
          <div>{{ $t("extTrainings.email.selected") }}:</div>
          <div class="mt-3">
            <v-card v-if="selectedEmails.length > 0" class="pa-3 mb-2">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    text
                    color="primary"
                    @click="clearEmails"
                    icon
                    style="position: absolute; top: 6px; right: 6px;"
                    v-on="on"
                    v-bind="attrs"
                  >
                    <v-icon>close</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("dialog.clear") }}</span>
              </v-tooltip>
              {{ selectedEmails.join(", ") }}
            </v-card>
          </div>
        </div>
      </template>

      <template v-slot:header.email="{ header }">
        <div class="d-flex align-center">
          <span>{{ header.text }}</span>
          <v-btn
            x-small
            color="primary"
            class="ml-2"
            @click.stop="selectAllEmails"
          >
            {{ $t("extTrainings.employees.selectAll") }}
          </v-btn>
        </div>
      </template>

      <template v-slot:header.manager.email="{ header }">
        <div class="d-flex align-center">
          <span>{{ header.text }}</span>
          <v-btn
            x-small
            color="primary"
            class="ml-2"
            @click.stop="selectAllManagerEmails"
          >
            {{ $t("extTrainings.employees.selectAll") }}
          </v-btn>
        </div>
      </template>

      <template v-slot:[`item.isSelected`]="{ item: e }">
        <v-simple-checkbox
          :value="e.isSelected"
          @input="toggleSelection(e)"
          :ripple="false"
        ></v-simple-checkbox>
      </template>

      <template v-slot:[`item.id`]="{ item: e }">
        <span :style="e.isActive ? '' : 'text-decoration: line-through;'">{{
          e.id | id
        }}</span>
      </template>

      <template v-slot:[`item.role`]="{ item: e }">{{
        $t("role." + e.role)
      }}</template>

      <template v-slot:[`item.lastName`]="{ item: e }">
        <router-link :to="{ name: 'employee', params: { id: e.id } }">{{
          e | name
        }}</router-link>
      </template>

      <template v-slot:[`item.manager.lastName`]="{ item: e }">
        <router-link
          v-if="e.manager"
          :to="{ name: 'employee', params: { id: e.manager.id } }"
          >{{ e.manager | name }}</router-link
        >
      </template>

      <template v-slot:[`item.email`]="{ item }">
        <v-chip
          v-if="item.email"
          @click="toggleEmail(item.email)"
          :color="isEmailSelected(item.email) ? 'primary' : 'transparent'"
          style="cursor: pointer;"
        >
          {{ item.email }}
        </v-chip>
      </template>

      <template v-slot:[`item.manager.email`]="{ item }">
        <v-chip
          v-if="item.manager && item.manager.email"
          @click="toggleEmail(item.manager.email)"
          :color="
            isEmailSelected(item.manager.email) ? 'primary' : 'transparent'
          "
          style="cursor: pointer;"
        >
          {{ item.manager.email }}
        </v-chip>
      </template>

      <template v-slot:[`item.department`]="{ item: e }">
        <span>{{ drawCategoryTags(e.department) }}</span>
      </template>

      <template v-slot:[`item.location`]="{ item: e }">
        <span>{{ drawCategoryTags(e.location) }}</span>
      </template>

      <template v-slot:[`item.position`]="{ item: e }">
        <span>{{ drawCategoryTags(e.position) }}</span>
      </template>

      <template v-slot:[`item.shift`]="{ item: e }">
        <span>{{ drawCategoryTags(e.shift) }}</span>
      </template>

      <template v-slot:[`item.collar`]="{ item: e }">
        <span>{{ drawCategoryTags(e.collar) }}</span>
      </template>

      <template v-slot:[`item.started`]="{ item: e }">
        <template v-if="e.started !== '1970-01-01T00:00:00Z'">{{
          e.started | date
        }}</template>
        <template v-else>{{ $t("employees.manuallyImported") }}</template>
      </template>

      <template v-slot:[`item.file`]="{ item: e }">
        <template v-if="e.file !== null">
          <div style="display: flex; gap: 5px; align-items: center;">
            <v-btn small outlined target="blank" :href="e.file.url">
              <v-icon small left>open_in_new</v-icon>
              {{ $t("instructions.preview") }}
            </v-btn>
            <v-btn x-small outlined fab @click="openDeleteDialog(e)">
              <v-icon small>delete</v-icon>
            </v-btn>
          </div>
        </template>
        <template v-if="e.file == null">
          <input
            type="file"
            accept="video/*, .pdf"
            ref="file"
            style="display: none"
            v-on:change="upload"
          />
          <v-btn
            v-if="role('admin', 'safetyRepresentative')"
            small
            class="primary"
            @click="uploadFile(e)"
            :loading="$apollo.loading || uploading"
          >
            <v-icon dark left>note_add</v-icon>
            {{ $t("extTrainings.certificate.add") }}
          </v-btn>
        </template>
      </template>

      <template
        v-if="showLastTraining"
        v-slot:[`item.lastExtTrainingPerCategory.id`]="{ item }"
      >
        {{ findRelevantTrainingName(item) }}
      </template>

      <template
        v-if="showLastTraining"
        v-slot:[`item.lastExtTrainingPerCategory`]="{ item }"
      >
        {{ findRelevantTrainingDueText(item.lastExtTrainingPerCategory) }}
        <br />
      </template>

      <template
        v-if="showLastTraining"
        v-slot:[`item.upcomingExtTrainingPerCategory.id`]="{ item }"
      >
        {{ findUpcomingRelevantTrainingName(item) }}
      </template>
    </v-data-table>
    <alert
      :alert="alert"
      :title="$t('extTrainings.certificate.delete')"
      :text="alert"
      :okText="$t('dialog.delete')"
      @cancel="alert = null"
      @ok="deleteFile()"
    />
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import UploadExternalTrainingEmployeeFile from "@/graphql/mutation/extTraining/UploadExternalTrainingEmployeeFile.gql";
import DeleteExternalTrainingEmployeeFileMutation from "@/graphql/mutation/extTraining/DeleteExternalTrainingEmployeeFile.gql";
import { getDetailedTagText } from "@/utils/tag";
import Alert from "@/components/Alert";

export default {
  props: [
    "employees",
    "selectedEmployees",
    "tags",
    "tagCategories",
    "loading",
    "showSelect",
    "categoryId",
    "showLastTraining",
    "selectedHeader",
    "showEmails",
  ],
  components: {
    Alert,
  },
  data() {
    return {
      filters: [],
      uploading: false,
      extTrainingEmployeeId: null,
      search: "",
      alert: null,
      extTrainingFileId: null,
      selectedEmails: [],
    };
  },
  watch: {
    filters(newVal) {
      this.$emit("filtersChange", newVal);
    },
  },
  computed: {
    ...mapGetters(["role"]),
    items() {
      return this.employees.map((e) => {
        if (this.showSelect) {
          return {
            ...e,
            isSelected: this.selectedEmployees.some((se) => se.id == e.id),
          };
        } else {
          return { ...e };
        }
      });
    },
    headers() {
      let h = [
        {
          text: this.$i18n.t("employees.column.id"),
          value: "id",
          class: "text-uppercase subheading",
          align: "right",
        },
        {
          text: this.$i18n.t("employees.column.name"),
          value: "lastName",
          class: "text-uppercase subheading",
        },
        {
          text: this.$i18n.t("employees.column.role"),
          value: "role",
          class: "text-uppercase subheading",
        },
        {
          text: this.$i18n.t("employees.column.started"),
          value: "started",
          class: "text-uppercase subheading text-truncate",
        },
      ];

      if (this.showSelect) {
        h.unshift({
          text: this.selectedHeader,
          value: "isSelected",
          align: "center",
          class: "text-uppercase subheading",
        });
      }

      this.tagCategories &&
        this.tagCategories.forEach((value, i) => {
          h.splice(4 + i, 0, {
            text: this.$i18n.t(`tags.${value}`),
            value: value,
            class: "text-uppercase subheading text-truncate",
          });
        });

      if (this.showLastTraining) {
        h.push(
          {
            text: this.$i18n.t("employees.column.lastExtTrainingPerCategory"),
            value: "lastExtTrainingPerCategory.id",
            class: "text-uppercase subheading text-truncate",
          },
          {
            text: this.$i18n.t(
              "employees.column.lastExtTrainingPerCategoryDue"
            ),
            value: "lastExtTrainingPerCategory",
            class: "text-uppercase subheading text-truncate",
            sort: (a, b) => this.customSort(a, b),
          },
          {
            text: this.$i18n.t(
              "employees.column.upcomingExtTrainingPerCategory"
            ),
            value: "upcomingExtTrainingPerCategory.id",
            class: "text-uppercase subheading text-truncate",
          }
        );
      } else {
        h.push({
          text: this.$i18n.t("extTrainings.column.certificate"),
          value: "file",
          class: "text-uppercase subheading text-truncate",
        });

        if (this.showEmails) {
          h.splice(
            3,
            0,
            {
              text: this.$i18n.t("extTrainings.column.email"),
              value: "email",
              class: "text-uppercase subheading text-truncate",
            },
            {
              text: this.$i18n.t("profile.manager"),
              value: "manager.lastName",
              class: "text-uppercase subheading text-truncate",
            },
            {
              text: this.$i18n.t("extTrainings.column.managerEmail"),
              value: "manager.email",
              class: "text-uppercase subheading text-truncate",
            }
          );
        }
      }
      return h;
    },
  },
  methods: {
    getDetailedTagText: getDetailedTagText,

    selectAll() {
      this.items.forEach((e) => (e.isSelected = true));
      this.$emit("selectedChange", this.items);
    },

    deselectAll() {
      this.items.forEach((e) => (e.isSelected = false));
      this.$emit("selectedChange", []);
    },

    toggleSelection(employee) {
      employee.isSelected = !employee.isSelected;

      const index = this.selectedEmployees.findIndex(
        (e) => e.id === employee.id
      );

      if (employee.isSelected && index === -1) {
        this.selectedEmployees.push(employee);
      } else if (!employee.isSelected && index !== -1) {
        this.selectedEmployees.splice(index, 1);
      }

      this.$emit("selectedChange", this.selectedEmployees);
    },

    customSort(a, b) {
      const aDays = this.calculateDueInDays(a);
      const bDays = this.calculateDueInDays(b);

      return aDays - bDays;
    },

    findRelevantTrainingName(e) {
      if (!e.lastExtTrainingPerCategory) {
        return "-";
      }
      return `#${e.lastExtTrainingPerCategory.id} ${e.lastExtTrainingPerCategory.name}`;
    },
    findUpcomingRelevantTrainingName(e) {
      if (!e.upcomingExtTrainingPerCategory) {
        return "-";
      }
      return `#${e.upcomingExtTrainingPerCategory.id} ${e.upcomingExtTrainingPerCategory.name}`;
    },
    findRelevantTrainingDueText(extTraining) {
      if (!extTraining) return "";
      if (extTraining.dueInMonths === 0) {
        return "-";
      }
      const dueDays = this.calculateDueInDays(extTraining);

      return this.validUntilDaysText(dueDays);
    },
    customFilter(value, search, item) {
      return (
        !search ||
        search
          .toLowerCase()
          .split(" ")
          .every((w) => item.search.includes(w))
      );
    },
    drawCategoryTags(tags) {
      return (
        (tags &&
          tags.length &&
          tags.map((tag) => tag.description || tag.name).join(", ")) ||
        ""
      );
    },
    validUntilDaysText(dueDays) {
      if (isNaN(dueDays)) return "-";

      return dueDays > 0
        ? this.$tc("extTrainings.dueInDaysOver", `${dueDays}`)
        : this.$tc("extTrainings.dueInDaysUntil", `${Math.abs(dueDays)}`);
    },
    calculateDueInDays(extTraining) {
      if (!extTraining) return -9999;
      const m = this.$moment(extTraining.dateTimeOfOccurrence);
      const endM = this.$moment(m).add(extTraining.dueInMonths, "month");
      const nowM = this.$moment();
      const duration = this.$moment.duration(nowM.diff(endM));
      return parseInt(duration.asDays());
    },
    uploadFile(e) {
      this.extTrainingEmployeeId = e.extTrainingEmployeeId;
      this.$refs.file.click();
    },
    upload(e) {
      const files = e.target.files || e.dataTransfer.files;
      const file = files.length && files[0];
      if (!file || !this.extTrainingEmployeeId) return;

      this.uploading = true;
      e.target.value = null;

      this.$apollo
        .mutate({
          mutation: UploadExternalTrainingEmployeeFile,
          variables: {
            id: this.extTrainingEmployeeId,
            file: file,
          },
        })
        .then(() => {
          this.$emit("fileChanged");
        })
        .catch((err) =>
          this.$store.commit("push_global_message", {
            color: "error",
            text: `${this.$i18n.t("instructions.edit.addFileFailed")}: ${err}`,
          })
        )
        .finally(() => {
          this.extTrainingEmployeeId = null;
          this.uploading = false;
        });
    },
    openDeleteDialog(e) {
      this.extTrainingEmployeeId = e.extTrainingEmployeeId;
      this.extTrainingFileId = e.file.id;
      this.alert = `${e.firstName} ${e.lastName}`;
    },
    deleteFile() {
      this.$apollo
        .mutate({
          mutation: DeleteExternalTrainingEmployeeFileMutation,
          variables: {
            employeeId: this.extTrainingEmployeeId,
            fileId: this.extTrainingFileId,
          },
        })
        .then(() => {
          this.$emit("fileChanged");
        })
        .catch((err) =>
          this.$store.commit("push_global_message", {
            color: "error",
            text: `${this.$i18n.t("error.saveFailed")}: ${err}`,
          })
        )
        .finally(() => {
          this.alert = null;
          this.extTrainingEmployeeId = null;
          this.extTrainingFileId = null;
        });
    },
    toggleEmail(email) {
      if (this.isEmailSelected(email)) {
        this.selectedEmails = this.selectedEmails.filter((e) => e !== email);
      } else {
        this.selectedEmails.push(email);
      }
    },

    selectAllEmails() {
      const filteredItems = this.items.filter((item) => {
        if (!this.search) return true;
        return this.customFilter(null, this.search, item);
      });

      filteredItems.forEach((item) => {
        if (item.email && !this.isEmailSelected(item.email)) {
          this.selectedEmails.push(item.email);
        }
      });
    },

    selectAllManagerEmails() {
      const filteredItems = this.items.filter((item) => {
        if (!this.search) return true;
        return this.customFilter(null, this.search, item);
      });

      filteredItems.forEach((item) => {
        if (
          item.manager &&
          item.manager.email &&
          !this.isEmailSelected(item.manager.email)
        ) {
          this.selectedEmails.push(item.manager.email);
        }
      });
    },

    isEmailSelected(email) {
      return this.selectedEmails.includes(email);
    },

    clearEmails() {
      this.selectedEmails = [];
    },
  },
};
</script>

<style>
.ext-training-ac.v-text-field.v-text-field--solo.v-input--dense
  > .v-input__control {
  max-height: 32px;
}
</style>
